import { CreateOrUpdateAnalysisButton } from '@modules/analysis/components/CreateOrUpdateAnalysisButton';
import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { SaveArtefactFunction } from '@modules/common/types';
import { QuantifySaveActionAlert } from '@modules/quantify/components/QuantifySaveActionAlert';
import { useHasChangesToBeSaved } from '@modules/quantify/hooks';
import { useGetQuantifyChartEQLProps } from '@modules/quantify/hooks/useFetchQuantifyChartData/useGetQuantifyChartEQLProps';
import { useGetQuantifyMetadataV2 } from '@modules/quantify/hooks/useGetQuantifyMetadataV2';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Typography } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

const trackAnalysisEvents = createAnalyticsTracker('analysisV3');
interface UpdateQuantifyActionProps {
  onAnalysisCreate: SaveArtefactFunction;
}

export const UpdateQuantifyAction = ({
  onAnalysisCreate,
}: UpdateQuantifyActionProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const eqlProps = useGetQuantifyChartEQLProps();

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const hasChangesToBeSaved = useHasChangesToBeSaved({
    shouldCheckForValidQuery: true,
  });

  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupByField?.displayName,
  );
  const shouldShowAnomalies = useQuantifyStore(
    ({ query }) => query.shouldShowAnomalies,
  );
  const useCase = useQuantifyStore(({ query }) =>
    query.compareQuery ? AnalysisUseCase.Compare : AnalysisUseCase.Default,
  );

  const { id, title, scope, editToken, createdBy, href } = useAnalysisStore(
    (analysis) => ({
      id: analysis.id,
      title: analysis.title,
      scope: analysis.scope,
      editToken: analysis.editToken,
      createdBy: analysis.createdBy,
      onUpdate: analysis.onUpdate,
      href: analysis.href,
    }),
    shallow,
  );

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { updateAnalysis, isLoading, error } = useUpdateAnalysis({
    name: title,
    scope: mapFromEdgeScope(scope),
    id: id,
    editToken: editToken,
    createdBy: createdBy,
    useCase,
  });

  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to fetch Quantify.`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const quantifyMetadataV2 = useGetQuantifyMetadataV2();

  const onQuantifyUpdate = async () => {
    try {
      await updateAnalysis({
        variables: {
          title,
          scope,
          id,
          editToken: editToken ?? '',
          createdBy: createdBy?.id,
          tenantID,
          metadataV2: {
            quantify: quantifyMetadataV2,
          },
          context: {
            eqlExpression: eql ?? '',
          },
        },
      });
      trackAnalysisEvents({
        eventName: 'Analysis Updated',
        name: title,
        scope: mapFromEdgeScope(scope),
        metadata: JSON.stringify(quantifyMetadataV2),
        link: href,
        useCase,
        quantifyQueryOptions: {
          showMeLabel: groupByLabel,
          shouldShowAnomalies,
        },
      });
      navigate(
        {
          search: '',
        },
        {
          state: location.state,
          replace: true,
        },
      );
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          hasQueryChanged: false,
        },
      });
      showSuccessNotification({
        contentComponent: `Updated the Quantify.`,
        props: { isClosable: true },
      });
    } catch {
      showErrorNotification({
        contentComponent: `Failed to update Quantify.`,
        props: { isClosable: true },
      });
    }
  };

  if (error) {
    return <Typography color="error">Failed to fetch Quantify</Typography>;
  }

  return (
    <CreateOrUpdateAnalysisButton
      isDisabled={!hasChangesToBeSaved}
      isLoading={isLoading}
      onAnalysisUpdate={onQuantifyUpdate}
      onAnalysisCreate={onAnalysisCreate}
      analysisFormProps={{ secondaryInfoNode: <QuantifySaveActionAlert /> }}
    />
  );
};
